import { TuiNotificationService } from "@taiga-ui/core";
/*
 * Copyright (c) 2020.
 * This file is part of the Compo B2B application.
 * (c) Compo soft - Vladimir Ganturin <gun2rin@gmail.com>
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import {isPlatformBrowser} from '@angular/common';
import {ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnDestroy, PLATFORM_ID} from '@angular/core';
import {ParamMap, Router} from '@angular/router';
import {NbDialogService, NbWindowService} from '@nebular/theme';
import {EMPTY, Observable, Subject} from 'rxjs';
import {catchError, filter, switchMap, take, tap} from 'rxjs/operators';
import {fadeAnimation} from '../animations/fade.animation';

import {Environment} from '../interfaces/Environment';
import {CompoApiService} from '../services/compo-api.service';
import {ConfirmDialogComponent} from './confirm-dialog/confirm-dialog.component';
import {ErrorDialogComponent} from './error-dialog/error-dialog.component';
import {HttpParams} from "@angular/common/http";
import {ActionButton} from './actions/actions.component'
import {translations} from '../translations.ru'

@Component({
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [fadeAnimation],
  standalone: false
})
export abstract class BaseComponent implements OnDestroy {
  loading = false;
  noPhotoSrc = '/assets/images/product/nofoto.png';
  actionButtons: ActionButton[] = [];
  readonly translations = translations;

  protected readonly unsubscribe: Subject<boolean> = new Subject<boolean>();
  protected readonly cdr = inject(ChangeDetectorRef);
  protected readonly dialogService = inject(NbDialogService);
  /** Диалоги существуют только в браузере — см. `handleError`. */
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  protected readonly windowService = inject(NbWindowService);
  protected readonly compoApiService = inject(CompoApiService);
  protected readonly router = inject(Router);
  readonly environment = inject(Environment);
  private readonly alerts = inject(TuiNotificationService);

  ngOnDestroy() {
    this.unsubscribe.next(true);
    this.unsubscribe.complete();
  }

  successToast(message: string) {
    this.alerts.open(message, {appearance: 'positive', autoClose: this.environment.toastrNotificationDuration}).subscribe();
  }

  warningToast(message: string) {
    this.alerts.open(message, {appearance: 'warning', autoClose: this.environment.toastrNotificationDuration}).subscribe();
  }

  errorToast(message: string) {
    this.alerts.open(message, {appearance: 'negative', autoClose: this.environment.toastrNotificationDuration}).subscribe();
  }

  handleError(err, silent = false) {
    console.error(err);
    this.loading = false;
    this.cdr.detectChanges();

    // На сервере модалку показывать некому, и она не безобидна: диалог строится прямо
    // в SSR-рендере, а на его сворачивании focus-trap Nebular зовёт `.focus()` у
    // несуществующего элемента → uncaughtException в Node и 504 вместо страницы.
    // Ловилось на витрине: `/list` при 403 от бэка отдавал 504, хотя должен был
    // отрисоваться пустым (`Cannot read properties of null (reading 'focus')`).
    // `console.error` выше на сервере остаётся — отказ виден в логах.
    if (!silent && this.isBrowser) {
      this.dialogService.open(ErrorDialogComponent, {
        context: {
          err: err
        }
      });
    }

    return EMPTY;
  }

  /**
   * Открывает диалог подтверждения и отдаёт результат (true / false / undefined).
   * Для случаев, когда нужна реакция и на «нет», или confirm встроен в реактивный поток.
   * Наследникам НЕ нужно инжектить NbDialogService — используйте confirm() / confirm$().
   */
  confirm$(message: string, options?: {confirmStatus?: string}): Observable<boolean> {
    return this.dialogService.open(ConfirmDialogComponent, {
      context: {
        description: message,
        confirmStatus: options?.confirmStatus ?? 'primary'
      },
    }).onClose;
  }

  confirm(message: string, callback: Observable<any>, options?: {confirmStatus?: string}) {
    this.confirm$(message, options).pipe(
      catchError(err => this.handleError(err)),
      filter(result => result === true),
      switchMap(_ => callback),
      tap(_ => this.cdr?.detectChanges()),
      take(1)
    ).subscribe();
  }

  createLabel(cnt: number, forms: string, withNum = true): string {
    const case1 = (cnt % 100 > 4 && cnt % 100 < 20);
    const case2 = (cnt % 10 < 5) ? cnt % 10 : 5;

    let ret = forms.split('|')[case1 ? 2 : [2, 0, 1, 1, 1, 2][case2]];
    if (withNum) {
      ret = cnt + ' ' + ret;
    }

    return ret;
  }

  get actualRouteParams() {
    return this.router.parseUrl(this.router.url).queryParams;
  }

  paramsToHttp(params: ParamMap) {
    let httpParams = new HttpParams();
    params.keys.forEach(key => {
      httpParams = httpParams.set(key, params.getAll(key).join(','));
    });

    return httpParams;
  }
}
